import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { IndianRupee, FileText, Loader2, CheckCircle2, Clock, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const inr = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;

function invoiceState(inv) {
  if (inv.status === "paid") return { label: "Paid", cls: "bg-status-available/15 text-status-available", icon: CheckCircle2 };
  if (inv.payment_submitted) return { label: "Awaiting confirmation", cls: "bg-status-pending/15 text-status-pending", icon: Clock };
  if (inv.overdue) return { label: "Overdue", cls: "bg-status-overdue/15 text-status-overdue", icon: AlertTriangle };
  return { label: "Due", cls: "bg-muted text-muted-foreground", icon: FileText };
}

function PayDialog({ inv, payment, onSubmitted }) {
  const [open, setOpen] = useState(false);
  const [ref, setRef] = useState("");
  const [busy, setBusy] = useState(false);
  const upiLink = payment?.upi_id
    ? `upi://pay?pa=${payment.upi_id}&pn=${encodeURIComponent(payment.payee_name || "")}&am=${inv.total}&cu=INR&tn=${inv.number}`
    : null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/operator/billing/invoices/${inv.id}/submit-payment`, { reference: ref, method: "upi" });
      toast.success("Payment submitted — awaiting confirmation");
      setOpen(false); onSubmitted?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`pay-invoice-${inv.id}`} size="sm" className="gap-1.5 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
          <IndianRupee className="h-3.5 w-3.5" /> Pay {inr(inv.total)}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Pay {inv.number}</DialogTitle>
          <DialogDescription>Scan with any UPI app to pay {payment?.payee_name || "the platform"} {inr(inv.total)}, then confirm below.</DialogDescription>
        </DialogHeader>
        {upiLink ? (
          <div className="mx-auto w-fit rounded-tenant-card border border-tenant-border bg-white p-3" data-testid="platform-upi-qr">
            <QRCodeSVG value={upiLink} size={200} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Platform UPI details are not configured yet — please contact Graphikpro.</p>
        )}
        <div className="rounded-md bg-muted px-3 py-2 text-center text-sm">
          UPI ID: <span className="font-medium">{payment?.upi_id || "—"}</span>
        </div>
        <div className="space-y-2">
          <label className="text-sm text-muted-foreground">UPI reference (optional)</label>
          <Input data-testid="payment-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. 4198XXXXXX" />
        </div>
        <Button data-testid={`submit-payment-${inv.id}`} onClick={submit} disabled={busy} className="gap-2 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} I've paid
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function OperatorBilling() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/operator/billing");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const { plan, usage, monthly_total, outstanding, invoices, payment, status, next_invoice_at } = data;

  return (
    <div data-testid="operator-billing-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your subscription with Graphikpro, invoices and how to pay.</p>
      </div>

      <div className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-5 shadow-tenant-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-tenant-primary" />
              <p className="font-semibold">{plan.name} plan</p>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${status === "active" ? "bg-status-available/15 text-status-available" : "bg-status-overdue/15 text-status-overdue"}`}>{status}</span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              {usage.vehicles}/{usage.vehicle_limit} vehicles{usage.vehicle_overage ? ` (+${usage.vehicle_overage} over)` : ""} · {usage.users}/{usage.user_limit} seats{usage.user_overage ? ` (+${usage.user_overage} over)` : ""}
            </p>
            {next_invoice_at && <p className="mt-1 text-xs text-muted-foreground">Next invoice ~{new Date(next_invoice_at).toLocaleDateString()}</p>}
          </div>
          <div className="text-right">
            <p className="text-2xl font-semibold">{inr(monthly_total)}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
            {outstanding > 0 && <p data-testid="billing-outstanding" className="text-xs text-status-overdue">{inr(outstanding)} outstanding</p>}
          </div>
        </div>
        <div className="mt-4 border-t border-tenant-border pt-3 text-xs text-muted-foreground">
          {usage.line_items.map((l) => `${l.label} ${inr(l.amount)}`).join("  ·  ")}
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">Invoices</p>
        {invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground">No invoices yet.</p>
        ) : invoices.map((inv) => {
          const st = invoiceState(inv);
          return (
            <div key={inv.id} data-testid={`my-invoice-${inv.id}`} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{inv.number} · {inr(inv.total)}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}><st.icon className="h-3 w-3" /> {st.label}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {(inv.line_items || []).map((l) => `${l.label} ${inr(l.amount)}`).join(" · ")}{inv.due_date ? ` · due ${new Date(inv.due_date).toLocaleDateString()}` : ""}
                  </p>
                  {inv.payment_submitted && inv.status !== "paid" && (
                    <p className="mt-1 text-xs text-status-pending">Submitted{inv.submitted_reference ? ` (ref ${inv.submitted_reference})` : ""} — Graphikpro will confirm shortly.</p>
                  )}
                </div>
                {inv.status !== "paid" && !inv.payment_submitted && (
                  <PayDialog inv={inv} payment={payment} onSubmitted={load} />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
